import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { LivePriceInfo } from '../../hooks/use-live-price';
import styles from './LivePriceView.module.scss';
import { MarketIndicator } from './market-indicator/MarketIndicator';

export interface LivePriceViewProps {
  livePriceInfo?: LivePriceInfo;
  lastKnownPrice?: number;
  datePresentation?: string;
  className?: string;
  formatPrice?: (price: number, decimals?: number) => string;
  showMarketIndicator?: boolean;
  showPriceChange?: boolean;
  lastTimestamp?: number;
  keepLayout?: boolean;
}

export const LivePriceView = ({
  livePriceInfo,
  lastKnownPrice,
  className,
  formatPrice,
  showMarketIndicator = true,
  showPriceChange = true,
  lastTimestamp,
  keepLayout = false,
}: LivePriceViewProps) => {
  const { t } = useTranslation('analyze');

  if ((!livePriceInfo && lastKnownPrice === undefined) || lastTimestamp === 0) {
    return null;
  }

  const livePrice =
    livePriceInfo?.lastKnownPrice !== undefined
      ? livePriceInfo.lastKnownPrice
      : lastKnownPrice;
  const price =
    livePriceInfo?.price !== undefined ? livePriceInfo.price : livePrice;

  const validPrice = price !== undefined;
  const validChange =
    livePriceInfo?.priceChange.status &&
    livePriceInfo?.priceChange.proportionChange;
  return (
    <div
      className={clsx(styles.livePriceBox, className)}
      data-testid="live-price-view"
    >
      {formatPrice && (
        <div className={styles.priceContainer} data-testid="price-container">
          {validPrice && (
            <span className={styles.livePrice} data-testid="price">
              {formatPrice(price)}
            </span>
          )}
        </div>
      )}
      {validChange && (
        <span
          data-testid="price-change"
          className={clsx(
            styles.priceChange,
            styles[livePriceInfo.priceChange.status]
          )}
        >
          {showPriceChange && (
            <>
              <span data-testid="price-change-value">
                {livePriceInfo.priceChange.change}
              </span>
              <span
                className={clsx(
                  styles.divider,
                  styles[livePriceInfo.priceChange.status]
                )}
              >
                /
              </span>
            </>
          )}
          <span data-testid="price-change-proportion">
            {livePriceInfo.priceChange.proportionChange}
          </span>
        </span>
      )}
      {!validChange && keepLayout && (
        <span
          className={clsx(styles.priceChange, styles.default)}
          data-testid="price-change-placeholder"
        />
      )}
      {showMarketIndicator && (
        <MarketIndicator
          className={styles.marketIndicatorStyled}
          isMarketOpen={!!livePriceInfo?.isLive}
          closeText={t('analyze:analysis.lastClose')}
          liveText={t('analyze:analysis.livePrice')}
        />
      )}
    </div>
  );
};
